// v14at-p4 F4 — Pure state-machine helpers voor measurement-jobs.
//
// Deze module heeft GEEN React-dependency en is volledig unit-testbaar
// als plain Node-script (zie `frontend/tests/measurement_jobs_fsm.test.js`).
//
// Belangrijke scheiding (user-aandachtspunt 2):
//   - `ui_state`      = afgeleid uit backend-status; wordt NOOIT door een
//                       frontend/network error gewijzigd. Backend blijft
//                       de bron van waarheid voor terminale states.
//   - `polling_health`= puur frontend-signaal; "ok" | "unreachable"
//                       | "stopped_horizon" | "stopped_errors". Wordt bij
//                       elke succesvolle poll teruggezet naar "ok".
//
// Een niet-terminale job met `polling_health !== "ok"` kan dus na reload /
// hernieuwde netwerkconnectie weer gesynchroniseerd worden met de backend.

const BACKEND_PENDING_STATES = new Set([
  "queued",
  "resolving",
  "fetching_sources",
  "measuring",
]);

const BACKEND_TERMINAL_STATES = new Set([
  "completed",
  "failed",
  "expired",
  "cancelled",
]);

const SUPERSEDE_REASONS = new Set([
  "superseded_by_address_change",
  "superseded_by_zone_deletion",
  "superseded_by_project_deletion",
]);

const DEFAULT_POLL_MS = 3000;
const MAX_POLL_MS = 15000;
const MAX_ERROR_STREAK = 10;
const DEFAULT_POLL_HORIZON_MS = 15 * 60 * 1000; // 15 min
const TERMINAL_RETAIN_MS = 5 * 60 * 1000; // 5 min

/**
 * Afleiding van de UI-state op basis van backend-status + eventuele
 * eerdere UI-state (voor sticky transities zoals "reviewed" en "applied").
 *
 * @param {object} backendJob Response van GET /jobs/{id}
 * @param {string} prevUiState Vorige UI-state ("creating" | "pending" | "completed"
 *                             | "reviewed" | "applying" | "applied" | "failed"
 *                             | "cancelled")
 * @returns {string} nieuwe ui_state
 */
export function deriveUiState(backendJob, prevUiState) {
  if (!backendJob || !backendJob.status) {
    // Zonder backend-data behouden we de vorige UI-state (bv. "creating"
    // voordat het eerste GET-response binnen is).
    return prevUiState || "creating";
  }
  const status = backendJob.status;

  // Terminale backend-states hebben voorrang, behalve 'reviewed' en 'applying'
  // en 'applied' die reeds terminale UI-states uitdrukken.
  if (prevUiState === "applied") return "applied";
  if (prevUiState === "applying" && status === "completed") return "applying";

  if (status === "failed") return "failed";
  if (status === "cancelled" || status === "expired") return "cancelled";
  if (status === "completed") {
    if (prevUiState === "reviewed") return "reviewed";
    return "completed";
  }
  if (BACKEND_PENDING_STATES.has(status)) {
    // Als user de review-dialog al open had en de polling blijkt dat de job
    // teruggezet is naar pending (bv. requeue) → terug naar pending.
    return "pending";
  }
  return prevUiState || "pending";
}

/**
 * Beslist of een job op dit moment gepoll't moet worden.
 */
export function shouldPoll(
  backendStatus,
  now,
  lastPolledAt,
  intervalMs,
  { pollingStartedAt = null, horizonMs = DEFAULT_POLL_HORIZON_MS, pollingHealth = "ok" } = {}
) {
  if (BACKEND_TERMINAL_STATES.has(backendStatus)) return false;
  if (pollingHealth === "stopped_horizon" || pollingHealth === "stopped_errors") return false;
  if (pollingStartedAt !== null && now - pollingStartedAt > horizonMs) return false;
  if (!lastPolledAt) return true;
  return now - lastPolledAt >= intervalMs;
}

/**
 * Bepaalt het volgende polling-interval op basis van backend-hint + fouten-streak.
 */
export function nextPollInterval(responseHintMs, errorStreak = 0) {
  const base = Number.isFinite(responseHintMs) && responseHintMs > 0
    ? Math.min(responseHintMs, MAX_POLL_MS)
    : DEFAULT_POLL_MS;
  if (errorStreak <= 0) return base;
  // Exponentiële backoff met factor 1.5 vanaf de hint-waarde.
  const backoff = Math.min(MAX_POLL_MS, Math.round(base * Math.pow(1.5, errorStreak)));
  return backoff;
}

/**
 * Herkent of een cancelled job in feite door de backend-supersede-hook
 * gecancelled werd (zone-adres gewijzigd / zone/project verwijderd).
 */
export function isSuperseded(backendJob) {
  if (!backendJob || backendJob.status !== "cancelled") return false;
  const reason = backendJob.status_reason;
  return !!reason && SUPERSEDE_REASONS.has(reason);
}

/**
 * Filter voor localStorage-hydrate.
 *
 * v14at-p4 F4.4 (Defect F) — Terminale `failed` en `completed` jobs mogen
 * NIET automatisch gewist worden tijdens hydratie. De gebruiker moet na
 * een reload/navigatie dezelfde foutmelding of reviewbare meting terug
 * zien, en de store moet die staat kunnen resynchroniseren met de
 * backend (die blijft source of truth).
 *
 * Alleen expliciet opgeruimde eindstaten (`applied`, `cancelled`) krijgen
 * een retention-window; na dat window worden ze stil opgeschoond.
 * Een terminale `expired`-markering (door backend of door een stale
 * reference) wordt ook opgeschoond.
 */
export function filterHydratedJobs(jobs, now = Date.now()) {
  const kept = {};
  for (const [jobId, entry] of Object.entries(jobs || {})) {
    const uiState = entry.ui_state;
    const isCleanupTerminal =
      uiState === "applied" ||
      uiState === "cancelled" ||
      uiState === "expired";
    if (!isCleanupTerminal) {
      // `queued`, `pending`, `measuring`, `completed`, `reviewed`,
      // `applying`, `failed` → altijd behouden; polling-loop beslist
      // zelf of er nog gepolled wordt via `shouldPoll(...)`.
      kept[jobId] = entry;
      continue;
    }
    const refTs = entry.completed_at || entry.updated_at || entry.created_at;
    const refMs = refTs ? Date.parse(refTs) : 0;
    if (refMs && now - refMs < TERMINAL_RETAIN_MS) {
      kept[jobId] = entry;
    }
  }
  return kept;
}

/**
 * Afleiding van `polling_health` op basis van een nieuwe gebeurtenis.
 *
 * Belangrijk: polling-fouten mogen NOOIT `ui_state=failed` zetten.
 * Deze helper levert enkel het frontend-signaal voor de UI-indicator.
 */
export function derivePollingHealth(event, { errorStreak = 0, pollingStartedAt = null, now = Date.now(), horizonMs = DEFAULT_POLL_HORIZON_MS } = {}) {
  if (pollingStartedAt !== null && now - pollingStartedAt > horizonMs) {
    return "stopped_horizon";
  }
  if (event === "ok") return "ok";
  if (event === "error") {
    if (errorStreak + 1 >= MAX_ERROR_STREAK) return "stopped_errors";
    return "unreachable";
  }
  return "ok";
}

export const _constants = {
  BACKEND_PENDING_STATES,
  BACKEND_TERMINAL_STATES,
  SUPERSEDE_REASONS,
  DEFAULT_POLL_MS,
  MAX_POLL_MS,
  MAX_ERROR_STREAK,
  DEFAULT_POLL_HORIZON_MS,
  TERMINAL_RETAIN_MS,
};
